import { Banner } from "@/components/flexkit/Banner";
import { AdaptivePagination } from "@/components/flexkit/AdaptivePagination";
import { Select } from "@/components/flexkit/select/Select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/flexkit/tables/Table";
import { cn } from "@/components/podkit/lib/cn";
import { Text } from "@/components/podkit/typography/Text";
import { TrackLocations } from "@/hooks/use-segment";
import {
    useSCIMOperationsLog,
    ResourceType,
    type SCIMOperationsFilter,
    type PlainAuditLogEntry,
} from "@/queries/scim-queries";
import { Principal } from "gitpod-next-api/gitpod/v1/identity_pb";
import { Timestamp } from "@bufbuild/protobuf";
import { useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState, type FC } from "react";

const PAGE_SIZE = 25;

type ResourceTypeFilterValue = "all" | "users" | "groups";

function toResourceTypeFilterValue(value: string): ResourceTypeFilterValue {
    if (value === "users" || value === "groups" || value === "all") {
        return value;
    }
    return "all";
}

const resourceTypeFilterOptions = [
    { value: "all", label: "All Resources" },
    { value: "users", label: "Users" },
    { value: "groups", label: "Groups" },
];

export const SCIMOperationsLog: FC = () => {
    // URL-based page state
    const [searchParams, setSearchParams] = useSearchParams();
    const currentPage = useMemo(() => {
        const page = searchParams.get("scim_page");
        return page ? parseInt(page, 10) - 1 : 0;
    }, [searchParams]);

    // Filter state
    const [selectedResourceType, setSelectedResourceType] = useState<ResourceTypeFilterValue>("all");

    // Build filter
    const filters = useMemo<SCIMOperationsFilter>(() => {
        if (selectedResourceType === "users") {
            return { resourceTypes: [ResourceType.USER] };
        }
        if (selectedResourceType === "groups") {
            return { resourceTypes: [ResourceType.GROUP] };
        }
        return {};
    }, [selectedResourceType]);

    // Reset to page 1 when filters change
    useEffect(() => {
        if (currentPage > 0) {
            const params = new URLSearchParams(searchParams);
            params.delete("scim_page");
            setSearchParams(params);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedResourceType]);

    // Fetch data
    const { data, isLoading, error, refetch } = useSCIMOperationsLog(filters, currentPage, PAGE_SIZE);

    const handlePageChange = (page: number) => {
        const params = new URLSearchParams(searchParams);
        if (page === 0) {
            params.delete("scim_page");
        } else {
            params.set("scim_page", String(page + 1));
        }
        setSearchParams(params);
    };

    return (
        <div className="flex flex-col gap-4" data-track-location={TrackLocations.SCIMOperationsLog}>
            {/* Filter Controls */}
            <div className="flex items-center gap-4">
                <Select
                    value={selectedResourceType}
                    onValueChange={(v) => setSelectedResourceType(toResourceTypeFilterValue(v))}
                    placeholder="All Resources"
                    className="min-w-44"
                    formatDisplayValue={(_, label) => <span>Resource: {label}</span>}
                    data-testid="scim-resource-type-filter"
                >
                    {resourceTypeFilterOptions.map((option) => (
                        <Select.Item key={option.value} value={option.value}>
                            {option.label}
                        </Select.Item>
                    ))}
                </Select>
                <Text className="text-sm text-content-tertiary">
                    Showing recent operations from your identity provider
                </Text>
            </div>

            {/* Error State */}
            {error && (
                <Banner
                    variant="danger"
                    text="Failed to load operations log"
                    action={{
                        text: "Retry",
                        onClick: () => refetch(),
                        "data-tracking-id": "scim-operations-log-retry",
                    }}
                />
            )}

            {/* Loading State */}
            {isLoading && (
                <div className="flex items-center justify-center py-12">
                    <Text className="text-content-secondary">Loading operations...</Text>
                </div>
            )}

            {/* Empty State */}
            {!isLoading && !error && data && data.entries.length === 0 && (
                <div className="flex flex-col items-center gap-4 py-12">
                    <Text className="text-content-secondary">No SCIM operations yet</Text>
                    <Text className="max-w-md text-center text-sm text-content-tertiary">
                        Operations will appear here when your identity provider provisions users and groups via SCIM.
                    </Text>
                </div>
            )}

            {/* Operations Table */}
            {!isLoading && !error && data && data.entries.length > 0 && (
                <>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-[180px]">Timestamp</TableHead>
                                <TableHead className="w-[200px]">Operation</TableHead>
                                <TableHead className="hidden w-[100px] md:table-cell">Type</TableHead>
                                <TableHead>Resource ID</TableHead>
                                <TableHead className="hidden w-[120px] md:table-cell">Actor</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {data.entries.map((entry) => (
                                <OperationsLogRow key={entry.id} entry={entry} />
                            ))}
                        </TableBody>
                    </Table>

                    {/* Pagination */}
                    {data.totalPages > 1 && (
                        <AdaptivePagination
                            currentPage={currentPage}
                            totalPages={data.totalPages}
                            totalCount={data.totalCount}
                            currentItemsCount={data.entries.length}
                            pageSize={PAGE_SIZE}
                            onPageChange={handlePageChange}
                        />
                    )}
                </>
            )}
        </div>
    );
};

// Table row component
const OperationsLogRow: FC<{ entry: PlainAuditLogEntry }> = ({ entry }) => {
    const timestamp = useMemo(() => {
        if (!entry.createdAt) return "N/A";
        const date = new Timestamp(entry.createdAt).toDate();
        return new Intl.DateTimeFormat("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
        }).format(date);
    }, [entry.createdAt]);

    const resourceType = useMemo(() => {
        switch (entry.subjectType) {
            case ResourceType.USER:
                return "User";
            case ResourceType.GROUP:
                return "Group";
            default:
                return "Unknown";
        }
    }, [entry.subjectType]);

    const actor = useMemo(() => {
        if (entry.actorPrincipal === Principal.SERVICE_ACCOUNT) {
            return "SCIM API";
        }
        return entry.actorId || "Unknown";
    }, [entry.actorPrincipal, entry.actorId]);

    const operationStyle = useMemo(() => {
        const action = entry.action?.toLowerCase() || "";
        if (action.includes("created")) {
            return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
        }
        if (action.includes("deleted")) {
            return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
        }
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
    }, [entry.action]);

    return (
        <TableRow>
            <TableCell>
                <Text className="font-mono text-sm text-content-secondary">{timestamp}</Text>
            </TableCell>
            <TableCell>
                <span
                    className={cn(
                        "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium",
                        operationStyle,
                    )}
                >
                    {entry.action}
                </span>
            </TableCell>
            <TableCell className="hidden md:table-cell">
                <Text className="text-sm">{resourceType}</Text>
            </TableCell>
            <TableCell>
                <Text className="max-w-[300px] truncate font-mono text-sm" title={entry.subjectId}>
                    {entry.subjectId}
                </Text>
            </TableCell>
            <TableCell className="hidden md:table-cell">
                <Text className="text-sm text-content-tertiary">{actor}</Text>
            </TableCell>
        </TableRow>
    );
};
